import { ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Eye } from 'lucide-react';

export interface UploadedFile {
    uid: string;
    name: string;
    /** data URL pour preview */
    thumbUrl?: string;
    url?: string;
    file?: File;
}

const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
    });

/**
 * Upload d'image avec vignette + aperçu plein écran — remplace antd Upload (picture-card).
 *
 * <FileUpload
 *   files={fileList}
 *   onChange={setFileList}
 *   accept="image/png,image/jpeg"
 *   maxCount={1}
 * />
 */
const FileUpload = (props: {
    files: UploadedFile[];
    onChange: (files: UploadedFile[]) => void;
    accept?: string;
    maxCount?: number;
    label?: ReactNode;
    hint?: ReactNode;
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const maxCount = props.maxCount ?? 1;

    const handlePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(e.target.files || []).slice(0, maxCount - props.files.length);
        const mapped: UploadedFile[] = [];
        for (const f of picked) {
            mapped.push({
                uid: `${Date.now()}-${f.name}`,
                name: f.name,
                file: f,
                thumbUrl: f.type.startsWith('image/') ? await getBase64(f) : undefined,
            });
        }
        props.onChange([...props.files, ...mapped]);
        if (inputRef.current) inputRef.current.value = '';
    };

    const remove = (uid: string) => props.onChange(props.files.filter(f => f.uid !== uid));

    return (
        <div className="w-full">
            {props.label && (
                <label className="block text-xs font-semibold text-[var(--eb-text-muted)] mb-1.5">{props.label}</label>
            )}
            <div className="flex flex-wrap gap-3">
                {props.files.map(f => (
                    <div key={f.uid} className="relative group w-24 h-24 rounded-xl border border-[var(--eb-border)] overflow-hidden bg-[var(--eb-bg)]">
                        {f.thumbUrl || f.url ? (
                            <img src={f.thumbUrl || f.url} alt={f.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-xs text-[var(--eb-text-muted)] px-2 text-center break-all">{f.name}</div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                            {(f.thumbUrl || f.url) && (
                                <button type="button" onClick={() => setPreview(f.thumbUrl || f.url || null)} className="p-1.5 text-white hover:bg-white/20 rounded-lg">
                                    <Eye size={16} />
                                </button>
                            )}
                            <button type="button" onClick={() => remove(f.uid)} className="p-1.5 text-white hover:bg-white/20 rounded-lg">
                                <X size={16} />
                            </button>
                        </div>
                    </div>
                ))}

                {props.files.length < maxCount && (
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="w-24 h-24 rounded-xl border-2 border-dashed border-[var(--eb-border)] hover:border-[var(--eb-primary)] hover:bg-[var(--eb-primary-soft)] transition-colors flex flex-col items-center justify-center gap-1 text-[var(--eb-text-muted)] hover:text-[var(--eb-primary)]"
                    >
                        <Plus size={22} />
                        <span className="text-[11px] font-medium">Charger</span>
                    </button>
                )}
            </div>
            {props.hint && <p className="mt-1.5 text-xs text-[var(--eb-text-muted)]">{props.hint}</p>}
            <input ref={inputRef} type="file" accept={props.accept} className="hidden" onChange={handlePick} multiple={maxCount > 1} />

            {preview && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setPreview(null)}>
                    <img src={preview} alt="preview" className="max-w-[90vw] max-h-[90vh] rounded-xl shadow-2xl" />
                    <button className="absolute top-4 right-4 p-2 text-white bg-white/10 hover:bg-white/20 rounded-lg" onClick={() => setPreview(null)}>
                        <X size={20} />
                    </button>
                </div>,
                document.body
            )}
        </div>
    );
};

export default FileUpload;
